import { createContext, useContext, useMemo } from "react";
import { filesystem_root, subdomain } from "../config";

// Provides the asset_sigs map (from book.json) and the URL base for the current
// presentation. Anything that constructs a file-server URL client-side calls
// useSignAssetUrl() to get a function that appends ?sig=… if the path is known.
export const AssetSigsContext = createContext({ sigs: {}, baseUrl: "" });

export const AssetSigsProvider = ({ presentation, isLive, children }) => {
  const value = useMemo(() => {
    if (!isLive || !presentation?.id) return { sigs: {}, baseUrl: "" };
    return {
      sigs: presentation.asset_sigs || {},
      baseUrl: `${filesystem_root}/${subdomain}/presentations/${presentation.id}/${presentation.id}`,
    };
  }, [presentation, isLive]);

  return (
    <AssetSigsContext.Provider value={value}>
      {children}
    </AssetSigsContext.Provider>
  );
};

// Returns a function that signs an already-built absolute URL. Looks up the
// relative path (URL minus baseUrl) in the sigs map; appends ?sig=… (or &sig=…
// if the URL already has a query) when a match is found. Returns the URL
// unchanged if there's no sig or the URL doesn't belong to this presentation.
export const useSignAssetUrl = () => {
  const { sigs, baseUrl } = useContext(AssetSigsContext);

  return (url) => {
    if (!url || !sigs || !baseUrl) return url;
    if (!url.startsWith(baseUrl + "/")) return url;

    const queryIdx = url.indexOf("?");
    const pathPart = queryIdx === -1 ? url : url.slice(0, queryIdx);
    const relPath = pathPart.slice(baseUrl.length + 1);

    // Already signed? leave alone (idempotent).
    if (queryIdx !== -1 && /[?&]sig=/.test(url)) return url;

    const sig = sigs[relPath];
    if (!sig) return url;

    const sep = queryIdx === -1 ? "?" : "&";
    return `${url}${sep}sig=${sig}`;
  };
};
